/**
 * AntarDristi — Gemini Flash API Client
 *
 * Calls Google Gemini Flash directly from the browser.
 * Keys never leave the user's machine.
 *
 * Accepts either a single API key or an ordered list. When given a list,
 * a 429 (rate limit) on one key transparently falls through to the next.
 * Other errors throw immediately so user mistakes (invalid key, bad request)
 * are surfaced rather than masked by the next key.
 */

// Gemini 2.5 Flash
const GEMINI_URL =
  'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent';

/**
 * Call Google Gemini Flash and return the text response.
 *
 * @param {string|string[]} apiKeyOrKeys - One key or an ordered fallback list
 * @param {string} systemPrompt          - Jyotish system instructions
 * @param {string} userPrompt            - Chart data / question
 * @returns {Promise<string>} Generated text
 */
export async function callGemini(apiKeyOrKeys, systemPrompt, userPrompt) {
  const keys = (Array.isArray(apiKeyOrKeys) ? apiKeyOrKeys : [apiKeyOrKeys])
    .map((k) => (k || '').trim())
    .filter(Boolean);

  if (keys.length === 0) {
    throw new Error('Gemini API key is not configured.');
  }

  const body = {
    contents: [
      {
        parts: [{ text: systemPrompt + '\n\n' + userPrompt }],
      },
    ],
    generationConfig: {
      temperature: 0.7,
      maxOutputTokens: 12000,
    },
  };

  let lastRateLimitError = null;

  for (let i = 0; i < keys.length; i++) {
    const apiKey = keys[i];
    let res;
    try {
      res = await fetch(`${GEMINI_URL}?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (err) {
      throw new Error(
        'Network error — could not reach Google Gemini. Check your connection.'
      );
    }

    if (res.ok) {
      const data = await res.json();
      const text = data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!text) {
        throw new Error(
          'Gemini returned an empty response. The model may have filtered the output.'
        );
      }
      return text;
    }

    let detail = '';
    try {
      const errBody = await res.json();
      detail = errBody?.error?.message || JSON.stringify(errBody);
    } catch {
      detail = res.statusText;
    }

    if (res.status === 429) {
      lastRateLimitError = detail;
      continue;
    }

    if (res.status === 400) {
      throw new Error('Invalid request. ' + detail);
    }
    if (res.status === 401 || res.status === 403) {
      throw new Error(
        'API key is invalid or does not have access. Please check your key.'
      );
    }
    throw new Error(`Gemini API error (${res.status}): ${detail}`);
  }

  throw new Error(
    `Rate limit reached on all ${keys.length} configured key${keys.length > 1 ? 's' : ''}. Please add another Gemini API key in AI Settings and try again.${lastRateLimitError ? ' (' + lastRateLimitError + ')' : ''}`
  );
}
